/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import BackSections from "../../Components/Giftcards/BackSections";
import PageError500 from "../../Components/PageError/PageError";
import Loader from "../../Components/Loader/Loader";
import CartItem from "../../Components/Cart/CartItem";
import CartSummary from "../../Components/Cart/CartSummary";
import CartEmpty from "../../Components/Cart/CartEmpty";
import CartActions from "../../Components/Cart/CartActions";
import CartExcludedItemsSheet from "../../Components/Cart/CartExcludedItemsSheet";
import { calculateCartSummary, isLinePurchasable } from "../../Components/Cart/cartPricing";
import useCartEarn from "../../Components/Cart/useCartEarn";
import useCartEnabled from "../../Components/Cart/useCartEnabled";
import { formatPrice, LoginV3Data, MaxQuan } from "../../Components/Utility/Utility";
import getTranslationData from "../../TranslationUtility/TranslationUtility";
import { encryptURLParam } from "../../Common/Axios/encrypt";
import {
  onAddToCart,
  onClearCartUpdateError,
  onRemoveCartLines,
  onFetchCart,
  REMOVING_LINES,
} from "../../Store/Slices/cartSlice";

// Per-line cap enforced by the cart API (CartSettings:MaxQuantityPerItem, 10 by default).
const MAX_QUANTITY_PER_LINE = Number(process.env.REACT_APP_CART_MAX_QTY_PER_ITEM) || 10;

/* The product's purchase limit for the limit types this client uses
   (purchaseLimitType, e.g. "Monthly"), the same rule as the product page. */
const findPurchaseLimit = (limits, limitTypes) => {
  const limit = (limits || []).find((item) =>
    limitTypes.includes(String(item?.limitType ?? "").trim().toLowerCase())
  );
  if (!limit) return null;
  const type = String(limit.limitType).trim();
  return { type: type.charAt(0).toUpperCase() + type.slice(1), value: Number(limit.limitValue) || 0 };
};

const Cart = () => {
  const translation = getTranslationData();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const isLogin = sessionStorage.getItem("login");
  const memberId = LoginV3Data()?.memberIdentifier;
  const { isCartEnabled, isCartConfigLoaded } = useCartEnabled();
  // Client-wide cap on gift cards per order, as used by the product page.
  const overallCapping = Number(MaxQuan()) || 0;
  const purchaseLimitType = useSelector(
    (state) => state.clientConfigurationReducer?.clientConfigurationData?.[0]?.purchaseLimitType
  );
  const {
    items,
    productDetailsBySku,
    cartLimitAmount,
    isLoading,
    isError,
    updatingLineId,
    updateFailed,
    updateError,
  } = useSelector((state) => state.cartReducer);

  // Login data and the client configuration can arrive after mount; the cart
  // API needs the member id and is never called when the cart is off.
  useEffect(() => {
    if (isLogin && isCartEnabled && memberId) dispatch(onFetchCart({ memberId }));
  }, [memberId, isCartEnabled]);

  // Add-to-cart failures (cart locked at checkout, quantity limit) show as a toast.
  useEffect(() => {
    if (!updateFailed) return;
    toast.error(updateError || translation?.cartUpdateError);
    dispatch(onClearCartUpdateError());
  }, [updateFailed]);

  const limitTypes = useMemo(
    () =>
      String(purchaseLimitType ?? "")
        .split(",")
        .map((type) => type.trim().toLowerCase())
        .filter(Boolean),
    [purchaseLimitType]
  );

  const lines = useMemo(
    () =>
      items.map((item) => {
        const details = item.sku ? productDetailsBySku?.[item.sku] : undefined;
        return {
          ...item,
          details,
          purchaseLimit: findPurchaseLimit(details?.limits, limitTypes),
        };
      }),
    [items, productDetailsBySku, limitTypes]
  );
  const summary = useMemo(() => calculateCartSummary(lines), [lines]);
  const { showEarnLabels, showWalletTotal, isEarnLabelShowOnly, getLineEarning, getTotalEarning } =
    useCartEarn();
  // Order earn shows where the product page shows it: with the earn labels, or on the wallet journey.
  const totalEarning = showEarnLabels || showWalletTotal ? getTotalEarning(lines) : null;
  const totalEarnStyle = showWalletTotal && !isEarnLabelShowOnly ? "points" : "label";
  // Out of stock / unavailable lines are left out of the order and removed before checkout.
  const excludedLines = useMemo(() => lines.filter((line) => !isLinePurchasable(line)), [lines]);
  const purchasableLines = useMemo(() => lines.filter(isLinePurchasable), [lines]);
  const [showExcludedSheet, setShowExcludedSheet] = useState(false);

  // The checkout page takes one product (with any of its denominations), so a
  // cart with several products cannot check out yet.
  const isSingleProductCheckout = new Set(purchasableLines.map((line) => line.productId)).size === 1;

  /* Opens the existing checkout page the same way the product page does:
     /card?productId=<id> with the chosen denominations in arrayData. */
  const proceedToCheckout = () => {
    const productId = String(purchasableLines[0]?.productId ?? "");
    if (!productId) return;
    const arrayData = [...purchasableLines]
      .sort((a, b) => a.denomination - b.denomination)
      .map((line) => {
        const discount = Number(line.details?.customerDiscount) || 0;
        return {
          denomination: line.denomination,
          you_pay: (line.denomination - (line.denomination * discount) / 100).toFixed(2),
          saving: ((line.denomination * discount) / 100).toFixed(2),
          quantity: line.quantity,
        };
      });
    const finalProductId =
      process.env.REACT_APP_IS_E_E === "false" ? productId : encryptURLParam(productId);
    navigate(`/card?productId=${finalProductId}`, { state: { arrayData } });
  };

  const handleCheckout = () => {
    if (!isSingleProductCheckout) {
      toast.error(translation?.cartCheckoutOneBrand);
      return;
    }
    if (excludedLines.length > 0) {
      setShowExcludedSheet(true);
      return;
    }
    proceedToCheckout();
  };

  // Continue from the sheet: out of stock / unavailable lines are set to quantity 0
  // first, and checkout starts only once that update succeeds.
  const [isRemovingExcluded, setIsRemovingExcluded] = useState(false);
  const handleContinueToCheckout = () => {
    if (!isSingleProductCheckout) {
      toast.error(translation?.cartCheckoutOneBrand);
      return;
    }
    setIsRemovingExcluded(true);
    dispatch(onRemoveCartLines({ memberId, lines: excludedLines }));
  };

  useEffect(() => {
    if (!isRemovingExcluded || updatingLineId) return;
    setIsRemovingExcluded(false);
    // On failure the sheet stays open and the error shows as a toast.
    if (!updateFailed) {
      setShowExcludedSheet(false);
      proceedToCheckout();
    }
  }, [isRemovingExcluded, updatingLineId, updateFailed]);
  const totalQuantity = items.reduce((total, item) => total + item.quantity, 0);

  // Highest Qty a line can go to: the per-line cap, and what the client's
  // order cap leaves after the other lines.
  const getMaxQuantity = (line) => {
    if (overallCapping <= 0) return MAX_QUANTITY_PER_LINE;
    const otherLinesQuantity = totalQuantity - line.quantity;
    return Math.min(MAX_QUANTITY_PER_LINE, Math.max(overallCapping - otherLinesQuantity, line.quantity));
  };
  const isOverCartLimit = cartLimitAmount > 0 && summary.totalDenomination > cartLimitAmount;

  // The cart is stored in Redis, so the chosen quantity is sent as-is and replaces the line's value.
  const handleQuantityChange = (line, nextQuantity) => {
    if (nextQuantity !== line.quantity) dispatch(onAddToCart({ memberId, line, qty: nextQuantity }));
  };

  // Remove uses the same API with quantity 0.
  const handleRemove = (line) => dispatch(onAddToCart({ memberId, line, qty: 0 }));

  const goShopping = () => navigate("/");

  // Wait for the client configuration before deciding the cart is off.
  if (isLogin && !isCartConfigLoaded) return <Loader />;

  // Not logged in, or the client has the cart off: the default 401 screen.
  if (!isLogin || !isCartEnabled) {
    return (
      <PageError500
        pageError={{
          StatusCode: "401",
          ErrorName: translation?.errorName,
          ErrorDescription: translation?.errorDescription,
          url: "/",
          buttonText: translation?.backToHome,
        }}
      />
    );
  }

  const renderContent = () => {
    if ((isLoading || !memberId) && items.length === 0) return <Loader />;
    if (isError) return <div className="cart-page__error">{translation?.cartLoadError}</div>;
    if (items.length === 0) return <CartEmpty translation={translation} onBrowse={goShopping} />;

    return (
      <div className="cart-page__content">
        <div className="cart-page__items">
          <h1 className="cart-page__heading">
            {lines.length} {lines.length === 1 ? translation?.cartItemInCart : translation?.cartItemsInCart}
          </h1>

          {isOverCartLimit && (
            <div className="cart-page__limit-banner">
              {translation?.cartLimitPrefix} ₹{formatPrice(cartLimitAmount)} {translation?.cartLimitSuffix}
            </div>
          )}

          <div className="cart-page__list">
            {lines.map((line) => (
              <CartItem
                key={line.lineId}
                line={line}
                translation={translation}
                maxQuantity={getMaxQuantity(line)}
                // Locked while the fresh cart loads, so a tap never acts on the previous visit's quantities.
                isUpdating={isLoading || updatingLineId === line.lineId || updatingLineId === REMOVING_LINES}
                earning={getLineEarning(line)}
                onQuantityChange={handleQuantityChange}
                onRemove={handleRemove}
              />
            ))}
          </div>
        </div>

        <div className="cart-page__side">
          <CartSummary
            summary={summary}
            translation={translation}
            earning={totalEarning}
            earnStyle={totalEarnStyle}
          />
          <CartActions
            translation={translation}
            onCheckout={handleCheckout}
            onContinueShopping={goShopping}
            isCheckoutDisabled={
              isLoading || summary.totalQuantity === 0 || isOverCartLimit || Boolean(updatingLineId)
            }
          />
        </div>
      </div>
    );
  };

  return (
    <div className="cart-page">
      <BackSections />
      <div className="cart-page__container">{renderContent()}</div>
      <CartExcludedItemsSheet
        show={showExcludedSheet}
        lines={excludedLines}
        amountPayable={summary.totalAmount}
        translation={translation}
        onClose={() => setShowExcludedSheet(false)}
        onContinue={handleContinueToCheckout}
        isContinuing={isRemovingExcluded}
      />
      <ToastContainer />
    </div>
  );
};

export default Cart;
